import React, { useEffect, type ReactNode } from 'react';
import { styled, css, keyframes } from '@compiled/react';
import groupBy from 'lodash/groupBy';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import Link from '@atlassian/jira-common-analytics-v2-wrapped-components/src/link';
import { borderRadius, layers } from '@atlassian/jira-common-styles/src';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl';
import type { Deployment } from '../../../../../../common/types';
import DeploymentIcon from '../../../../../../common/ui/deployment-icon';
import messages from './messages';
import { formatDeploymentDate, useSlideOut } from './utils';

const DATE_FORMAT = {
	year: 'numeric',
	month: 'long',
	day: 'numeric',
	hour: 'numeric',
	minute: 'numeric',
} as const;
type Props = {
	deployments: Deployment[];
	onShowMoreInfo: () => void;
	onClose: () => void;
};

type DeploymentByPipelinePair = [string, Deployment[]];

const DeploymentsDialogContent = ({ onClose, deployments, onShowMoreInfo }: Props) => {
	const { formatMessage, formatDate } = useIntl();

	const slideOut = useSlideOut();

	useEffect(() => {
		let timer: NodeJS.Timeout | undefined;
		if (slideOut !== null) {
			timer = setTimeout(() => {
				onClose();
			}, ANIMATION_DURATION);
		}
		return () => {
			clearTimeout(timer);
		};
	}, [onClose, slideOut]);

	const isMultiDeployment = deployments.length > 1;
	const isSingleDeployment = deployments.length === 1;
	const isManyDeployments = deployments.length > 3;
	const deploymentsByPipeline = groupBy(
		deployments,
		(deployment) => deployment.pipeline.displayName,
	);

	const deploymentsByPipelineEntries: DeploymentByPipelinePair[] =
		Object.entries(deploymentsByPipeline);

	return (
		// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type '"UP" | "LEFT" | "RIGHT" | null'.
		<ContentContainer slideOut={slideOut}>
			{isMultiDeployment && (
				<Header manyDeployments={isManyDeployments}>
					<Heading size="small" as="h3">
						<DeploymentsTitle>
							{formatMessage(messages.numDeployments, {
								numDeployments: deployments.length,
							})}

							<DateText>
								{deployments[0].lastUpdated.toLocaleDateString(undefined, {
									month: 'long',
									day: 'numeric',
								})}
							</DateText>
						</DeploymentsTitle>
					</Heading>
				</Header>
			)}

			<DeploymentsContainer>
				{deploymentsByPipelineEntries.map(
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					([pipelineName, pipelineDeployments]: [any, any]) => (
						<React.Fragment key={pipelineName}>
							{isMultiDeployment && (
								<PipelineLink href={pipelineDeployments[0].pipeline.url} target="_blank">
									{pipelineName}
								</PipelineLink>
							)}

							{pipelineDeployments.map((deployment: Deployment) => (
								<DeploymentContainer
									// used for end-to-end pollinator test (https://pollinator.prod.atl-paas.net/checks/a221dc5e-7252-497b-b39a-e6a1169cfe52)
									data-test-id="software-deployments.ui.timeline.deployments-bar.circles.deployments-dialog.dialog-content.deployment"
									data-testid="software-deployments.ui.timeline.deployments-bar.circles.deployments-dialog.dialog-content.deployment"
									key={deployment.id}
								>
									{isSingleDeployment && (
										<>
											<>
												<Heading level="h200">
													<Label>{formatMessage(messages.pipeline)}</Label>
												</Heading>
												<Link href={deployment.pipeline.url} target="_blank">
													{deployment.pipeline.displayName}
												</Link>
											</>

											<>
												<Heading level="h200">
													<Label>{formatMessage(messages.environment)}</Label>
												</Heading>
												<FlexBox>
													<IconWrapper>
														<DeploymentIcon
															deployments={[deployment]}
															showMultipleDeploymentsIcon={false}
															hasBorder={false}
														/>
													</IconWrapper>
													{deployment.environment.displayName}
												</FlexBox>
											</>

											<>
												<Heading level="h200">
													<Label>{formatMessage(messages.updated)}</Label>
												</Heading>
												<>
													<Tooltip
														content={formatDate(deployment.lastUpdated, DATE_FORMAT)}
														position="top"
													>
														<span>{formatDeploymentDate(new Date(), deployment.lastUpdated)}</span>
													</Tooltip>
												</>
											</>
											<>
												<Heading level="h200">
													<Label>{formatMessage(messages.deployment)}</Label>
												</Heading>
												<Link href={deployment.url} target="_blank">
													{deployment.displayName}
												</Link>
											</>
										</>
									)}

									{isMultiDeployment && (
										<SpaceBetween>
											<FlexBox>
												<IconWrapper>
													<DeploymentIcon
														deployments={[deployment]}
														showMultipleDeploymentsIcon={false}
														hasBorder={false}
													/>
												</IconWrapper>
												{deployment.environment.displayName}
											</FlexBox>
											<LastUpdated>
												<Tooltip
													content={formatDate(deployment.lastUpdated, DATE_FORMAT)}
													position="top"
												>
													<span>{formatDeploymentDate(new Date(), deployment.lastUpdated)}</span>
												</Tooltip>
											</LastUpdated>
										</SpaceBetween>
									)}
								</DeploymentContainer>
							))}
						</React.Fragment>
					),
				)}
			</DeploymentsContainer>
			<Footer>
				<FooterContent>
					<Button
						testId="software-deployments.ui.timeline.deployments-bar.circles.deployments-dialog.dialog-content.show-more-info"
						shouldFitContainer
						onClick={() => {
							onShowMoreInfo();
						}}
					>
						{formatMessage(messages.showMoreInfo)}
					</Button>
				</FooterContent>
			</Footer>
		</ContentContainer>
	);
};

const gutter = token('space.300', '24px');
const smallGutter = token('space.200', '16px');

const slideLeft = keyframes({
	from: {
		opacity: 1,
		transform: 'translateX(0px)',
	},
	to: {
		opacity: 0,
		transform: 'translateX(-50px)',
	},
});

const slideRight = keyframes({
	from: {
		opacity: 1,
		transform: 'translateX(0px)',
	},
	to: {
		opacity: 0,
		transform: 'translateX(50px)',
	},
});

const slideUp = keyframes({
	from: {
		opacity: 1,
		transform: 'translateY(0px)',
	},
	to: {
		opacity: 0,
		transform: 'translateY(-50px)',
	},
});

const slideAnimation = {
	LEFT: slideLeft,
	RIGHT: slideRight,
	UP: slideUp,
} as const;

export type SlideAnimation = keyof typeof slideAnimation;
const ANIMATION_DURATION = 350; // milliseconds
type ContentContainerProps = {
	slideOut: SlideAnimation | null;
	children: ReactNode;
};

const DIALOG_MAX_HEIGHT = 376;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentContainer = styled.div<ContentContainerProps>(
	{
		width: '360px',
		height: '100%',
		maxHeight: `${DIALOG_MAX_HEIGHT}px`,
		display: 'flex',
		flexDirection: 'column',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		zIndex: layers.layer,
		overflow: 'auto',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('elevation.surface.overlay', colors.N0),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		borderRadius: `${borderRadius}px`,
		boxShadow: token(
			'elevation.shadow.overlay',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			`0 4px 8px -2px ${colors.N50A}, 0 0 1px ${colors.N60A}`,
		),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props: ContentContainerProps) =>
		props.slideOut &&
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		css({
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			animation: `${slideAnimation[props.slideOut]} ${ANIMATION_DURATION}ms ease-in-out`,
			animationFillMode: 'forwards',
			pointerEvents: 'none',
		}),
);

interface HeaderProps {
	manyDeployments: boolean;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.div<HeaderProps>(
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('elevation.surface.overlay', colors.N0),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.manyDeployments &&
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		css({
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			borderBottom: `1px solid ${token('color.border', colors.N20A)}`,
		}),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DeploymentsTitle = styled.span({
	padding: gutter,
	display: 'flex',
	justifyContent: 'space-between',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N800),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DeploymentsContainer = styled.div({
	padding: `0 ${gutter}`,
	overflowY: 'auto',
	flexGrow: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Footer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface.overlay', colors.N0),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterContent = styled.div({
	padding: smallGutter,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderTop: `1px solid ${token('color.border', colors.N20A)}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlexBox = styled.div({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'left',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpaceBetween = styled.div({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DeploymentContainer = styled.div({
	paddingBottom: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PipelineLink = styled.a({
	marginBottom: token('space.200', '16px'),
	display: 'block',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LastUpdated = styled.small({
	marginTop: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N200),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	paddingRight: token('space.100', '8px'),
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DateText = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N200),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Label = styled.div({
	marginBottom: 0,
});

export default DeploymentsDialogContent;
